import { useEffect, useMemo, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { PmsType, type User } from '@/@types/global';
import type { Patient } from '@/modules/annotator/types';
import { VisitSidebar } from '@/modules/annotator/visitSidebar/VisitSidebar';

import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { ExternalPatientContext, ONBOARDING_VIDEO_KEY } from '../constants';
import { ExternalError } from '../ExternalError';
import { FreeTrialBanner } from '../FreeTrialBanner';
import {
  useGetTrialStatus,
  useIsWithinThreeBusinessDaysOfStart,
} from '../hooks';
import type { ImageSelectedMessage } from '../types';
import { IframeMessageType, TrialStatus } from '../types';
import { useExternalPatientData } from '../useExternalPatientData';

import { ManageSubscription } from './ManageSubscription';
import { OnboardingBanner } from './OnboardingBanner';
import { TrialExpiredCollapsible } from './TrialExpiredCollapsible';
import { TrialOfferCollapsible } from './TrialOfferCollapsible';
import { VisitSidebarMenu } from './VisitSidebarMenu';

const SidePanelLayout = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={classNames(
      'vai-w-full vai-h-full vai-bg-sidebar-background vai-flex vai-flex-col',
      className,
    )}
  >
    {children}
  </div>
);

export const ExternalVisitSidebarHandler = () => {
  const location = useLocation();
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const reduxPatients = useSelector(
    (state: { annotator?: { patients?: Record<string, Patient> } }) =>
      state.annotator?.patients,
  );

  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const pmsType = user?.practice?.pmsType;

  const urlParams = new URLSearchParams(location.search);
  const examId = urlParams.get('examId');
  const externalPatientId = urlParams.get('externalPatientId');

  const [isOpenOnboardingBanner, setIsOpenOnboardingBanner] = useState(true);
  const [isManageSubscription, setIsManageSubscription] = useState(false);
  const [externalImageId, setExternalImageId] = useState<string | null>(
    urlParams.get('imageId'),
  );

  const {
    imageIdData,
    patientIdData,
    isLoadingPatientData,
    isErrorPatientData,
    visitSummaries,
    latestImages,
    isLoadingVisitAndImageData,
    isErrorVisitAndImageData,
  } = useExternalPatientData({ externalPatientId, externalImageId });
  const {
    data: trialStatusData,
    isLoading: isLoadingTrialStatus,
    isError: isErrorTrialStatus,
  } = useGetTrialStatus();
  const isSuccess =
    !!patientIdData && !!imageIdData && !!visitSummaries && !!latestImages;

  const activeImage = useMemo(() => {
    const images = Object.values(
      reduxPatients?.[patientIdData?.patientId ?? '']?.images ?? {},
    );
    return images?.find((image) => image.imageId === imageIdData?.videaImageId);
  }, [imageIdData?.videaImageId, patientIdData?.patientId, reduxPatients]);

  useEffect(() => {
    if (
      user?.practice && // we want the PMS type to be present, so have to make sure that practice is present
      (!externalPatientId ||
        !externalImageId ||
        isSuccess ||
        isErrorPatientData ||
        isErrorVisitAndImageData)
    ) {
      trackEvent(mixpanelEvents.externalPmsEntry, {
        source: 'visit-sidebar',
        externalPatientId,
        externalImageId,
        examId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
      });
    }
    // Disabling exhaustive deps so that event is only tracked once after internal patient and image data is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isErrorPatientData, isErrorVisitAndImageData, user?.practice]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<ImageSelectedMessage>) => {
      const eventType = event.data?.type;
      if (eventType !== IframeMessageType.IMAGE_SELECTED) return;

      const payload = event.data?.payload;
      const newExternalImageId = payload?.imageId;
      if (newExternalImageId) {
        trackEvent(mixpanelEvents.externalImageSelected, {
          externalPatientId,
          externalImageId: newExternalImageId,
          patientId: patientIdData?.patientId ?? null,
        });
        setExternalImageId(newExternalImageId);
      } else {
        console.warn('No image ID received');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [externalPatientId, patientIdData?.patientId, trackEvent]);

  useEffect(() => {
    // Once all data is loaded, send message to notify RCI that images can now be selected
    if (isSuccess || isErrorPatientData || isErrorVisitAndImageData) {
      trackEvent(mixpanelEvents.externalContentLoaded, {
        externalPatientId,
        externalImageId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
      });
      window.parent.postMessage(
        {
          type: IframeMessageType.CONTENT_LOADED,
          payload: {},
        },
        '*',
      );
    }
    // Disabling exhaustive deps so that message is only sent when the content first loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isErrorPatientData, isErrorVisitAndImageData]);

  const trialStatus = trialStatusData?.status;
  const isFreeTrial = trialStatus === TrialStatus.TRIAL;
  const isTrialOffered = trialStatus === TrialStatus.TRIAL_ELIGIBLE;
  const isTrialExpired =
    trialStatus === TrialStatus.TRIAL_EXPIRED ||
    trialStatus === TrialStatus.CANCEL_REQUEST_TRIAL;
  const isTrialOfferedOrExpired = isTrialOffered || isTrialExpired;
  const isWithinThreeBusinessDaysOfStart =
    useIsWithinThreeBusinessDaysOfStart(trialStatusData);
  const isOnboardingVideoWatched = !!localStorage.getItem(ONBOARDING_VIDEO_KEY);
  const showOnboardingBanner =
    isWithinThreeBusinessDaysOfStart &&
    isOpenOnboardingBanner &&
    !isOnboardingVideoWatched;

  const getLogo = () => {
    return pmsType === PmsType.DENTRIX_ASCEND ? (
      <img
        alt="Dentrix Ascend VideaAI Logo"
        className="vai-h-5 [@media(max-height:570px)]:vai-h-4 vai-self-end"
        src="/assets/images/ddai/2025-dentrix-ascend-videaai.svg"
      />
    ) : (
      <img
        alt="Dentrix VideaAI Logo"
        className="vai-h-5 [@media(max-height:570px)]:vai-h-4 vai-self-end"
        src="/assets/images/ddai/2025-dentrix-videaai.svg"
      />
    );
  };

  const visitSidebarView = () => {
    if (isLoadingTrialStatus) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-[3px] vai-border-t-transparent vai-border-sapphire-600" />
        </SidePanelLayout>
      );
    }

    if (!externalPatientId || !externalImageId) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`We didn't receive enough information about the patient or image.`}
            source="VISIT_SIDEBAR"
            title={t`Not enough information.`}
          />
        </SidePanelLayout>
      );
    }

    if (isErrorPatientData) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`The patient information we received didn't match a patient in Detect AI.`}
            source="VISIT_SIDEBAR"
            title={t`We can't find this patient.`}
          />
        </SidePanelLayout>
      );
    }

    if (isErrorVisitAndImageData || isErrorTrialStatus) {
      return (
        <SidePanelLayout className="vai-p-4 vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`Please refresh the panel. If the problem continues, contact support for help.`}
            source="VISIT_SIDEBAR"
            title={t`Trouble loading content.`}
          />
        </SidePanelLayout>
      );
    }

    if (isTrialOfferedOrExpired) {
      return (
        <SidePanelLayout className="vai-justify-between vai-py-3 vai-px-2">
          {getLogo()}
          {isTrialOffered && <TrialOfferCollapsible />}
          {isTrialExpired && <TrialExpiredCollapsible />}
        </SidePanelLayout>
      );
    }

    if (isManageSubscription) {
      return (
        <SidePanelLayout>
          <ManageSubscription
            isLoading={isLoadingTrialStatus}
            onClose={() => {
              trackEvent(mixpanelEvents.externalManageSubscriptionClose, {
                externalPatientId,
                externalImageId,
                patientId: patientIdData?.patientId ?? null,
                imageId: imageIdData?.videaImageId ?? null,
              });
              setIsManageSubscription(false);
            }}
            trialStatusData={trialStatusData}
          />
        </SidePanelLayout>
      );
    }

    return (
      <SidePanelLayout>
        {isFreeTrial && <FreeTrialBanner trialStatusData={trialStatusData} />}

        <div className="vai-relative vai-flex-1 vai-pt-4 [@media(max-height:570px)]:vai-pt-2 vai-flex vai-flex-col vai-justify-between vai-overflow-auto">
          <div className="vai-px-2 vai-flex vai-items-center vai-justify-between">
            {getLogo()}
            <div className="vai-flex vai-items-center vai-gap-2">
              {/* TODO: Add back in when content is ready */}
              {/* {isFreeTrial && <AIImpactButton />} */}
              <VisitSidebarMenu
                trialStatus={trialStatus}
                onManageSubscription={() => {
                  trackEvent(mixpanelEvents.externalManageSubscriptionOpen, {
                    externalPatientId,
                    externalImageId,
                    patientId: patientIdData?.patientId ?? null,
                    imageId: imageIdData?.videaImageId ?? null,
                  });
                  setIsManageSubscription(true);
                }}
              />
            </div>
          </div>

          <VisitSidebar
            activeImage={activeImage}
            dob={latestImages?.dob}
            isExpandedSidebar
            isLoading={isLoadingPatientData || isLoadingVisitAndImageData}
            visitSummary={visitSummaries?.[0]}
          />

          {showOnboardingBanner && (
            <OnboardingBanner
              trialStatusData={trialStatusData}
              onClose={() => {
                setIsOpenOnboardingBanner(false);
              }}
            />
          )}
        </div>
      </SidePanelLayout>
    );
  };

  return (
    <ExternalPatientContext.Provider
      value={{
        isIframe: true,
        externalPatientId,
        externalImageId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
      }}
    >
      {visitSidebarView()}
    </ExternalPatientContext.Provider>
  );
};
